import React, { useMemo, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/apiClient";
import { useLang } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Database, HardDrive, RefreshCcw, RotateCcw, ScanSearch, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CONFIRM = "DELETE PERMANENTLY";

const bytes = (value = 0) => {
  if (value < 1024) return `${value} B`;
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 ** 2).toFixed(2)} MB`;
};

const labelFor = (item) => item.filename || item.summary?.name_id || item.summary?.name_en ||
  item.summary?.name || item.summary?.email || item.summary?.anonymous_code || item.summary?.slug || item.id;

const cleanupStatus = (item) => {
  if (item.reason_code === "referenced_asset") return "Protected — currently used";
  if (item.reason_code === "historical_reference_only") return "Historical only — safe to purge";
  if (item.reason_code === "unreferenced_asset") return "Unused — safe to purge";
  if (item.classification === "unknown") return "Unknown — manual review";
  return item.classification.replaceAll("_", " ");
};

function Metric({ label, value, detail }) {
  return <div className="rounded-xl border border-border bg-card p-4">
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="mt-1 text-2xl font-display">{value}</div>
    {detail && <div className="mt-1 text-xs text-muted-foreground">{detail}</div>}
  </div>;
}

function ConfirmDialog({ action, onClose }) {
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const response = await action.run(CONFIRM);
      if (response?.data?.ok === false) throw new Error("Some items could not be deleted. Review the refreshed scan and try again.");
      onClose(true);
    }
    catch (error) { toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || error.message); }
    finally { setBusy(false); }
  };
  return <Dialog open onOpenChange={(open) => !open && onClose(false)}>
    <DialogContent>
      <DialogHeader><DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="w-5 h-5" />{action.title}</DialogTitle><DialogDescription>{action.description}</DialogDescription></DialogHeader>
      <div className="space-y-3 text-sm">
        <p className="font-medium">This cannot be undone.</p>
        <p className="text-muted-foreground">Type <span className="font-mono text-foreground">{CONFIRM}</span> to continue.</p>
        <Input value={phrase} onChange={(event) => setPhrase(event.target.value)} data-testid="maintenance-confirm-input" />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
        <Button className="bg-destructive text-white" disabled={phrase !== CONFIRM || busy} onClick={submit} data-testid="maintenance-confirm-submit">Delete permanently</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}

function ScanHeader({ title, description, scannedAt, onScan, loading, testid }) {
  return <div className="flex items-start justify-between gap-4">
    <div><h2 className="font-display text-xl">{title}</h2><p className="text-sm text-muted-foreground mt-1">{description}</p>{scannedAt && <p className="text-xs text-muted-foreground mt-2">Last scan: {new Date(scannedAt).toLocaleString()}</p>}</div>
    <Button variant="outline" onClick={onScan} disabled={loading} data-testid={testid}><ScanSearch className="w-4 h-4 mr-2" />{loading ? "Scanning…" : "Scan"}</Button>
  </div>;
}

export default function Maintenance() {
  const { lang } = useLang();
  const [trash, setTrash] = useState(null);
  const [storage, setStorage] = useState(null);
  const [temporary, setTemporary] = useState(null);
  const [loading, setLoading] = useState("");
  const [selectedStorage, setSelectedStorage] = useState(new Set());
  const [selectedTemporary, setSelectedTemporary] = useState(new Set());
  const [confirmAction, setConfirmAction] = useState(null);

  const load = async (kind) => {
    setLoading(kind);
    try {
      const path = kind === "trash" ? "/admin/maintenance/trash" : `/admin/maintenance/${kind}/scan`;
      const { data } = await api.get(path);
      if (kind === "trash") setTrash(data);
      if (kind === "storage") { setStorage(data); setSelectedStorage(new Set()); }
      if (kind === "temporary") { setTemporary(data); setSelectedTemporary(new Set()); }
    } catch (error) { toast.error(error?.response?.data?.detail || error.message); }
    finally { setLoading(""); }
  };

  const safeStorage = useMemo(() => (storage?.items || []).filter((item) => item.classification === "safe_to_purge"), [storage]);
  const safeTemporary = useMemo(() => (temporary?.items || []).filter((item) => item.classification === "safe_to_purge"), [temporary]);
  const keyFor = (item) => `${item.kind}:${item.id}`;
  const toggle = (setter, key) => setter((current) => {
    const next = new Set(current); next.has(key) ? next.delete(key) : next.add(key); return next;
  });

  const restore = async (item) => {
    try { await api.post(`/admin/maintenance/trash/${item.entity_type}/${item.id}/restore`); toast.success("Restored"); load("trash"); }
    catch (error) { toast.error(error?.response?.data?.detail || error.message); }
  };
  const purgeTrash = (item) => setConfirmAction({
    title: "Delete trash item permanently",
    description: `Permanently remove ${labelFor(item)}?`,
    run: (confirm_phrase) => api.post(`/admin/maintenance/trash/${item.entity_type}/${item.id}/purge`, { items: [], confirm_phrase }),
    refresh: "trash",
  });
  const purgeSelection = (kind, data, selected) => {
    const items = (data?.items || []).filter((item) => selected.has(keyFor(item))).map(({ kind: itemKind, id }) => ({ kind: itemKind, id }));
    if (!items.length) return;
    setConfirmAction({
      title: "Purge selected items",
      description: `Permanently remove ${items.length} selected item${items.length === 1 ? "" : "s"}? The server will check every item again before deletion.`,
      run: (confirm_phrase) => api.post(`/admin/maintenance/${kind}/purge`, { items, confirm_phrase }),
      refresh: kind,
    });
  };
  const closeConfirm = (completed) => {
    const refresh = confirmAction?.refresh;
    setConfirmAction(null);
    if (completed) { toast.success("Cleanup completed"); load(refresh); }
  };

  return <AdminLayout>
    <div className="mb-8">
      <h1 className="font-display text-3xl tracking-tight">{lang === "id" ? "Pemeliharaan" : "Maintenance"}</h1>
      <p className="text-sm text-muted-foreground mt-1">Review recoverable records and reclaim only storage that is safe to remove.</p>
    </div>
    <Tabs defaultValue="trash">
      <TabsList className="mb-6">
        <TabsTrigger value="trash" data-testid="maintenance-tab-trash"><Trash2 className="w-4 h-4 mr-2" />Trash</TabsTrigger>
        <TabsTrigger value="storage" data-testid="maintenance-tab-storage"><HardDrive className="w-4 h-4 mr-2" />Storage Cleanup</TabsTrigger>
        <TabsTrigger value="temporary" data-testid="maintenance-tab-temporary"><Database className="w-4 h-4 mr-2" />Temporary Data</TabsTrigger>
      </TabsList>

      <TabsContent value="trash" className="space-y-5">
        <ScanHeader title="Trash" description="Recover or permanently remove intentionally deleted records." scannedAt={trash?.scanned_at} onScan={() => load("trash")} loading={loading === "trash"} testid="maintenance-scan-trash" />
        {trash && <><div className="grid sm:grid-cols-2 gap-3"><Metric label="Items" value={trash.count} /><Metric label="Estimated size" value={bytes(trash.bytes)} /></div>
          <div className="rounded-2xl border border-border bg-card divide-y divide-border">{trash.items.map((item) => <div key={`${item.entity_type}:${item.id}`} className="p-4 flex items-center gap-4" data-testid="trash-item">
            <div className="min-w-0 flex-1"><div className="font-medium truncate">{labelFor(item)}</div><div className="text-xs text-muted-foreground mt-1">{item.entity_type} · {item.deleted_at ? new Date(item.deleted_at).toLocaleString() : "date unavailable"} · {bytes(item.estimated_bytes)}{item.deleted_by ? ` · by ${item.deleted_by}` : ""}</div>{!item.can_purge && <Badge variant="outline" className="mt-2">Protected dependency</Badge>}</div>
            <Button size="sm" variant="outline" onClick={() => restore(item)}><RotateCcw className="w-4 h-4 mr-1" />Restore</Button>
            {item.can_purge && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => purgeTrash(item)}>Delete permanently</Button>}
          </div>)}{trash.items.length === 0 && <div className="p-10 text-center text-muted-foreground">Trash is empty.</div>}</div></>}
      </TabsContent>

      <TabsContent value="storage" className="space-y-5">
        <ScanHeader title="Storage Cleanup" description="Find unused assets, orphan objects, and incomplete uploads. Scanning never deletes data." scannedAt={storage?.scanned_at} onScan={() => load("storage")} loading={loading === "storage"} testid="maintenance-scan-storage" />
        {storage && <><div className="grid sm:grid-cols-4 gap-3"><Metric label="Safe items" value={storage.totals.safe_to_purge.count} detail={bytes(storage.totals.safe_to_purge.bytes)} /><Metric label="Protected" value={storage.totals.protected.count} /><Metric label="GridFS objects" value={storage.gridfs.object_count} /><Metric label="File metadata" value={storage.application_file_count} /></div>
          <div className="flex gap-2"><Button variant="outline" onClick={() => setSelectedStorage(new Set(safeStorage.map(keyFor)))}>Select all safe</Button><Button className="bg-destructive text-white" disabled={!selectedStorage.size} onClick={() => purgeSelection("storage", storage, selectedStorage)}>Purge selected</Button></div>
          <StorageGroup title="Unused Assets" description="Uploaded asset metadata checked against all supported references." items={storage.items.filter((item) => item.kind === "file_metadata")} selected={selectedStorage} onToggle={(key) => toggle(setSelectedStorage, key)} />
          <StorageGroup title="Orphan GridFS Objects" description="Logical storage objects no longer connected to application metadata or generated cards." items={storage.items.filter((item) => item.kind === "gridfs_object")} selected={selectedStorage} onToggle={(key) => toggle(setSelectedStorage, key)} />
          <StorageGroup title="Failed or Incomplete Uploads" description="Unowned GridFS versions are purgeable only after a 24-hour safety window." items={storage.items.filter((item) => item.kind === "gridfs_version")} selected={selectedStorage} onToggle={(key) => toggle(setSelectedStorage, key)} />
        </>}
      </TabsContent>

      <TabsContent value="temporary" className="space-y-5">
        <ScanHeader title="Temporary Data" description="Find expired system records with a proven cleanup rule. Results, payments, accounts, and audit history are excluded." scannedAt={temporary?.scanned_at} onScan={() => load("temporary")} loading={loading === "temporary"} testid="maintenance-scan-temporary" />
        {temporary && <><div className="grid sm:grid-cols-3 gap-3"><Metric label="Safe items" value={temporary.totals.safe_to_purge.count} detail={bytes(temporary.totals.safe_to_purge.bytes)} /><Metric label="Protected" value={temporary.totals.protected.count} /><Metric label="Reclaimable" value={bytes(temporary.totals.safe_to_purge.bytes)} /></div>
          <div className="flex gap-2"><Button variant="outline" onClick={() => setSelectedTemporary(new Set(safeTemporary.map(keyFor)))}>Select all safe</Button><Button className="bg-destructive text-white" disabled={!selectedTemporary.size} onClick={() => purgeSelection("temporary", temporary, selectedTemporary)}>Purge selected</Button></div>
          <CleanupTable items={temporary.items} selected={selectedTemporary} onToggle={(key) => toggle(setSelectedTemporary, key)} />
        </>}
      </TabsContent>
    </Tabs>
    {confirmAction && <ConfirmDialog action={confirmAction} onClose={closeConfirm} />}
  </AdminLayout>;
}

function CleanupTable({ items, selected, onToggle }) {
  return <div className="rounded-2xl border border-border bg-card overflow-hidden"><div className="divide-y divide-border">{items.map((item) => {
    const key = `${item.kind}:${item.id}`;
    const safe = item.classification === "safe_to_purge";
    return <div key={key} className="p-4 flex items-center gap-4" data-testid="cleanup-item">
      <Checkbox checked={selected.has(key)} disabled={!safe} onCheckedChange={() => onToggle(key)} aria-label={`Select ${labelFor(item)}`} />
      <div className="min-w-0 flex-1"><div className="font-medium truncate">{labelFor(item)}</div><div className="text-xs text-muted-foreground mt-1">{item.kind.replaceAll("_", " ")} · {bytes(item.bytes)}</div>{item.kind === "file_metadata" && <><div className="text-xs text-muted-foreground mt-1">Live references: {item.live_reference_count || 0} · Historical references: {item.historical_reference_count || 0}</div><ReferenceDetails item={item} /></>}</div>
      <Badge variant="outline" className={safe ? "border-sage-300 bg-sage-50 text-teal-800" : ""}>{cleanupStatus(item)}</Badge>
      <span className="hidden md:block text-xs text-muted-foreground max-w-56 truncate">{item.reason_code}</span>
    </div>;
  })}{items.length === 0 && <div className="p-10 text-center text-muted-foreground"><RefreshCcw className="w-5 h-5 mx-auto mb-2" />No items found.</div>}</div></div>;
}

function ReferenceDetails({ item }) {
  const references = [
    ...(item.live_references || []).map((reference) => ({ ...reference, scope: "Current" })),
    ...(item.historical_references || []).map((reference) => ({ ...reference, scope: "Historical version" })),
  ];
  if (!references.length) return null;
  return <details className="mt-2 text-xs text-muted-foreground">
    <summary className="cursor-pointer">Show reference details</summary>
    <div className="mt-2 space-y-2">{references.map((reference, index) => <div key={`${reference.scope}:${reference.collection}:${reference.record_id}:${reference.field}:${index}`} className="rounded-md bg-muted/50 p-2">
      <div className="font-medium text-foreground">{reference.scope} · {reference.record_label || reference.record_id || reference.collection}</div>
      <div>{reference.collection}{reference.record_id ? ` · ${reference.record_id}` : ""}</div>
      <div className="font-mono break-all">{reference.field}</div>
    </div>)}</div>
  </details>;
}

function StorageGroup({ title, description, ...tableProps }) {
  return <section className="space-y-2"><div><h3 className="font-medium">{title}</h3><p className="text-xs text-muted-foreground">{description}</p></div><CleanupTable {...tableProps} /></section>;
}

export { bytes };
